import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { useEventLogging } from '../../../../models/Logging';
import { ToolbarBlog } from '../../../../models/UiUtils';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useImageAttachmentMutation } from '../../../../queries';
import { AIImageDialog } from '../../../components-v2/ai/AIImageDialog';
import { AiButton } from '../../../components-v2/AiButton';
import { ImageFileChooserModal } from '../../../components/ImageFileChooserModal';
import { ImagePreview } from '../../../components/ImagePreview';
import { ImageUploadButtons } from '../../../components/ImageUploadButtons';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { MediaChooserModal } from '../../../components/MediaChooser';
import { DefaultHighResHorizontalPixabayImageSearchOptions } from '../../../components/MediaChooser/models';
import { IModalProps } from '../../../components/Modal';
import { TextInput } from '../../../components/TextInput';
import {
	IRichContentDocumentEditorConfig,
	RichContentDocumentEditor,
} from '../../../components/richContent/RichContentDocumentEditor';
import { ImageStage, REDUCER_ACTION_KEYS, useBlogPostEditorContext } from './hooks';
import { styleSheet } from './styles';

const defaultEditorConfig: IRichContentDocumentEditorConfig = {
	plugins: 'autolink',
	toolbar: ToolbarBlog,
	validStyles: { ['*']: '' }, // don't allow any styles
};

export interface IProps extends IImpersonationContextComponentProps {
	onAIAssitantLinkClick?: () => void;
	onEditorLoad?: () => void;
}

function BlogPostEditorBase({ onAIAssitantLinkClick, impersonationContext, onEditorLoad }: IProps) {
	const { state, dispatch } = useBlogPostEditorContext();
	const userSession = useUserSession();
	const { logApiError } = useEventLogging('BlogPostEditor');
	const errorMessages = useErrorMessages();

	const onFreeImageChooserRequestCloseRef = React.useRef<(img?: Api.IFileAttachment, cancel?: boolean) => void>(null);
	const [freeImageModalProps, setFreeImageModalProps] = React.useState<IModalProps>({
		isOpen: false,
		onRequestClose: (result, cancel) => onFreeImageChooserRequestCloseRef.current?.(result, cancel),
	});

	// AI Image Dialog state
	const [imageDialogOpen, setImageDialogOpen] = React.useState(false);
	const [generateImageDialogOpen, setGenerateImageDialogOpen] = React.useState(false);
	const [imageToConversationMap, setImageToConversationMap] = React.useState<Record<string, string>>({});
	const [pendingGenerateConversationId, setPendingGenerateConversationId] = React.useState<string | null>(null);

	const modalContent = React.useMemo(() => {
		const _content = state.content.getRawRichTextContent();
		// remove all tags to use as content for the image search modal
		const contentString = _content.document.replace(/<[^>]*>?/gm, '');
		return contentString;
	}, [state.content]);

	const hasImageSelected = Boolean(state.selectedImage);

	React.useEffect(() => {
		if (
			state.content.getRawRichTextContent().document.length &&
			!state.selectedImage &&
			state.imageState === ImageStage.Loading
		) {
			onClickFreeImage();
			toast.success('Select a blog image');
		}
	}, [state.selectedImage, state.imageState, state.content]);

	const imageAttachmentMutation = useImageAttachmentMutation({
		onError: (error: Api.IOperationResultNoValue): void => {
			errorMessages.pushApiError(error);

			toast.error('Failed to upload image');
			logApiError('UploadImageEventRegistrationSurvey-Error', error);
		},
		onSuccess: result => {
			dispatch({ type: REDUCER_ACTION_KEYS.IMAGE_ATTACH_SUCCESS, payload: { image: result[0] } });
		},
	});

	const handleFileInputModalClose = (val?: File | null, cancel?: boolean) => {
		dispatch({ type: REDUCER_ACTION_KEYS.TOGGLE_FILE_INPUT_MODAL, payload: { open: false } });
		if (val && !cancel) {
			imageAttachmentMutation.mutate({ files: [val], impersonationContext });
		}
	};

	const resetImage = () => {
		dispatch({ type: REDUCER_ACTION_KEYS.IMAGE_SELECTED, payload: { image: null } });
	};

	const onContentEditorStateChanged = (contentEditorState: Api.IRichContentEditorState) => {
		dispatch({ type: REDUCER_ACTION_KEYS.SET_CONTENT, payload: { content: contentEditorState } });
	};

	const onClickMedia = () => {
		dispatch({ type: REDUCER_ACTION_KEYS.TOGGLE_FILE_INPUT_MODAL, payload: { open: true } });
	};

	function onClickFreeImage() {
		setFreeImageModalProps(value => {
			return {
				...value,
				isOpen: true,
			};
		});
	}

	const handleOpenImageDialog = React.useCallback(() => {
		setImageDialogOpen(true);
	}, []);

	const handleOpenGenerateDialog = React.useCallback(() => {
		setGenerateImageDialogOpen(true);
	}, []);

	const handleConversationCreated = React.useCallback(
		(conversationId: string) => {
			if (state.selectedImage?.id) {
				setImageToConversationMap(prev => ({
					...prev,
					[state.selectedImage.id]: conversationId,
				}));
			}
		},
		[state.selectedImage?.id]
	);

	const onFreeImageChooserRequestClose = React.useCallback(
		(imageFile?: Api.IFileAttachment, cancel?: boolean) => {
			if (cancel || !imageFile) {
				setFreeImageModalProps(value => {
					return {
						...value,
						isOpen: false,
					};
				});
				dispatch({
					type: REDUCER_ACTION_KEYS.GENERATING_CONTENT,
					payload: {
						imageState: ImageStage.Loaded,
						imageSuggestingLoading: false,
					},
				});
				return;
			}
			dispatch({ type: REDUCER_ACTION_KEYS.IMAGE_ATTACH_SUCCESS, payload: { image: imageFile } });
			setFreeImageModalProps(value => {
				return {
					...value,
					isOpen: false,
				};
			});
		},
		[dispatch]
	);

	onFreeImageChooserRequestCloseRef.current = onFreeImageChooserRequestClose;

	return (
		<>
			{state.status !== Api.BlogStatus.Pending ? (
				<>
					{onAIAssitantLinkClick && userSession.account?.features?.contentGeneration?.enabled ? (
						<div className={css(styleSheet.aiAssistantWrap)}>
							<AiButton onClick={onAIAssitantLinkClick} title='Write blog with AI' size='xs' />
						</div>
					) : null}
				</>
			) : null}
			<div className={css(styleSheet.formControl)}>
				<label htmlFor='title'>
					Title <sup>*</sup>
				</label>
				<TextInput
					inputId='title'
					name='title'
					type='text'
					value={state.title}
					onChange={e => dispatch({ type: REDUCER_ACTION_KEYS.SET_TITLE, payload: { title: e.target.value } })}
				/>
			</div>
			<div className={css(styleSheet.formControl, styleSheet.contentBlock)}>
				<label htmlFor='content'>
					Blog Content <sup>*</sup>
				</label>
				<RichContentDocumentEditor
					className={css(styleSheet.editor)}
					config={defaultEditorConfig}
					contentState={state.content}
					onLoad={onEditorLoad}
					onContentStateChanged={onContentEditorStateChanged}
				/>
			</div>
			<div>
				{!state.imageSuggestingLoading ? (
					<>
						{!hasImageSelected ? (
							<>
								<label>Add Media</label>
								<ImageUploadButtons
									hasImage={hasImageSelected}
									onClickFreeImage={() => onClickFreeImage()}
									onClickMedia={onClickMedia}
									onGenerateClick={handleOpenGenerateDialog}
								/>
							</>
						) : (
							<section>
								<h2 className={css(styleSheet.subTitle)}>
									{state.suggestedImage ? 'Ai suggested image' : 'Selected Image'}
								</h2>
								{state.selectedImage ? (
									<ImagePreview
										fileName={state.selectedImage?.fileName}
										fileSize={state.selectedImage?.fileSize}
										source={state.selectedImage?.source}
										onRemove={resetImage}
										onEdit={handleOpenImageDialog}
										url={state.selectedImage?.url}
									/>
								) : null}
							</section>
						)}
					</>
				) : (
					<section>
						<figure className={css(styleSheet.loadingImages)}>
							<LoadingSpinner type='large' />{' '}
							<figcaption style={{ marginLeft: 8 }}>Loading suggested images...</figcaption>
						</figure>
					</section>
				)}
			</div>
			<ImageFileChooserModal
				modalProps={{
					isOpen: state.fileInputModalOpen,
					onRequestClose: handleFileInputModalClose,
				}}
			/>
			{freeImageModalProps.isOpen ? (
				<MediaChooserModal
					content={modalContent ?? undefined}
					imageOnly={true}
					impersonationContext={impersonationContext}
					modalProps={freeImageModalProps}
					onSave={onFreeImageChooserRequestClose}
					styles={[styleSheet.mediaModalStyle]}
					useBasicSearch={false}
					imageSearchOptions={DefaultHighResHorizontalPixabayImageSearchOptions}
				/>
			) : null}

			<AIImageDialog
				open={imageDialogOpen}
				onOpenChange={setImageDialogOpen}
				impersonationContext={impersonationContext}
				initialImages={state.selectedImage ? [state.selectedImage] : undefined}
				setImages={updater => {
					// When editing an image, replace the current image with the new one (blogs only support 1 image)
					const newImages =
						typeof updater === 'function' ? updater(state.selectedImage ? [state.selectedImage] : []) : updater;

					if (newImages && newImages.length > 0) {
						const newImage = newImages[newImages.length - 1]; // Get the latest generated image

						// Associate the new image with the same conversation for iterative editing
						if (state.selectedImage?.id && imageToConversationMap[state.selectedImage.id]) {
							setImageToConversationMap(prev => ({
								...prev,
								[newImage.id]: imageToConversationMap[state.selectedImage.id],
							}));
						}

						dispatch({ type: REDUCER_ACTION_KEYS.IMAGE_ATTACH_SUCCESS, payload: { image: newImage } });
					}
				}}
				existingConversationId={state.selectedImage ? imageToConversationMap[state.selectedImage.id] : undefined}
				onConversationCreated={handleConversationCreated}
			/>

			<AIImageDialog
				open={generateImageDialogOpen}
				onOpenChange={setGenerateImageDialogOpen}
				impersonationContext={impersonationContext}
				setImages={updater => {
					// When generating a new image, replace any existing image (blogs only support 1 image)
					const newImages = typeof updater === 'function' ? updater([]) : updater;

					if (newImages && newImages.length > 0) {
						const newImage = newImages[0]; // Get the first generated image

						// Associate the pending conversation with the newly generated image
						if (pendingGenerateConversationId) {
							setImageToConversationMap(prev => ({
								...prev,
								[newImage.id]: pendingGenerateConversationId,
							}));
							setPendingGenerateConversationId(null);
						}

						dispatch({ type: REDUCER_ACTION_KEYS.IMAGE_ATTACH_SUCCESS, payload: { image: newImage } });
					}
				}}
				onConversationCreated={conversationId => {
					setPendingGenerateConversationId(conversationId);
				}}
			/>
		</>
	);
}

export const BlogPostEditor = inject(ImpersonationContextKey)(observer(BlogPostEditorBase));
